import { createRunMutationAction, createLoadingAction } from '@/store/utils';
import * as types from '@/store/registration/actionTypes';
import * as mutationTypes from '@/store/registration/mutationTypes';
// eslint-disable-next-line import/no-cycle
import {
  registerUser,
  loadIndustries,
  loadRegulations,
  loadRegions,
  loadAmountOfEmployees,
  loadCompanySuggest,
  verifyEmail,
  requestEmailVerification,
  loadCybersecurityKnowhowOptions,
  findUserPoolId,
  requestEmailVerificationForSP,
  verifyEmailForSP,
  getCompanyInfo, validateInvitationCode,
} from '@/services/repository';
import {
  CLEAN_INVITATION_CODE_DATA,
  SET_IS_USER_POOL_EXISTS,
} from '@/store/registration/actionTypes';
import {
  SET_COMPANY_REGISTRATION_DATA, SET_INVITATION_VALIDATION,
} from '@/store/registration/mutationTypes';
import moment from 'moment';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import i18n from '../../i18n/index';

const getTimeWithMinuteOffset = offset => moment()
  .startOf('minute')
  .add(offset, 'minute')
  .format('HH:mm');

const formattedTimeZone = getGroupedTimezonesList().map(tz => ({
  value: tz.offset,
  name: tz.name,
  id: tz.id,
}));

const currentTimeZone = formattedTimeZone.find(timezone => timezone.value === String(moment().utcOffset()));

export default {
  [types.CHANGE_WORKSPACE_ID]: createRunMutationAction(mutationTypes.SET_WORKSPACE_ID),
  [types.CHANGE_CLIENT_AVATAR]: createRunMutationAction(mutationTypes.SET_CLIENT_AVATAR),
  [types.CLEAN_REGISTRATION_DATA]: createRunMutationAction(mutationTypes.REMOVE_REGISTRATION_DATA),
  [types.CLEAN_INVITATION_CODE_DATA]: createRunMutationAction(mutationTypes.REMOVE_INVITATION_CODE_DATA),
  [types.CLEAN_REGISTRATION_USER_EMAIL]: createRunMutationAction(mutationTypes.CLEAN_REGISTRATION_USER_EMAIL),
  [types.CHANGE_REGISTRATION_CURRENT_STEP]: createRunMutationAction(mutationTypes.SET_REGISTRATION_CURRENT_STEP),
  [types.CHANGE_COMPANY_REGISTRATION_DATA]: createRunMutationAction(mutationTypes.SET_COMPANY_REGISTRATION_DATA),
  [types.CHANGE_CLIENT_REGISTRATION_DATA]: createRunMutationAction(mutationTypes.SET_CLIENT_REGISTRATION_DATA),
  [types.UPDATE_RECAPTCHA_FOR_INVITATION_CODE]: createRunMutationAction(mutationTypes.SET_INVITATION_VALIDATION_RECAPTCHA_TOKEN),
  [types.CHANGE_REGISTRATION_RECAPTCHA_TOKEN]: createRunMutationAction(mutationTypes.SET_REGISTRATION_RECAPTCHA_TOKEN),
  [types.CHANGE_REGISTRATION_FILLED_STEPS_COUNT]: createRunMutationAction(
    mutationTypes.SET_REGISTRATION_FILLED_STEPS_COUNT,
  ),
  [types.CHANGE_REGISTRATION_VISITED_STEPS_COUNT]: createRunMutationAction(
    mutationTypes.SET_REGISTRATION_VISITED_STEPS_COUNT,
  ),
  [types.LOAD_USER_REGISTRATION_INFO]({ dispatch }) {
    dispatch(types.LOAD_CYBERSECURITY_KNOWHOW_OPTIONS);
  },
  [types.LOAD_COMPANY_REGISTRATION_INFO]({ dispatch }) {
    dispatch(types.LOAD_INDUSTRIES);
    dispatch(types.LOAD_REGULATIONS);
    dispatch(types.LOAD_OFFICE_LOCATIONS);
    dispatch(types.LOAD_AMOUNT_OF_EMPLOYEES);
  },
  [types.LOAD_COMPANY_SUGGESTIONS]: createLoadingAction({
    method: loadCompanySuggest,
    dataKey: 'companySuggestions',
    settingDataMutation: mutationTypes.SET_COMPANY_SUGGESTIONS,
    settingLoadingMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING,
    settingErrorMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING_ERRORS,
    settingDefaultDataMutation: mutationTypes.SET_COMPANY_SUGGESTIONS,
  }),
  [types.LOAD_INDUSTRIES]: createLoadingAction({
    method: loadIndustries,
    dataKey: 'industries',
    settingDataMutation: mutationTypes.SET_INDUSTRIES,
    settingLoadingMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING,
    settingErrorMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING_ERRORS,
  }),

  [types.LOAD_REGULATIONS]: createLoadingAction({
    method: loadRegulations,
    dataKey: 'regulations',
    settingDataMutation: mutationTypes.SET_REGULATIONS,
    settingLoadingMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING,
    settingErrorMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING_ERRORS,
  }),
  [types.LOAD_OFFICE_LOCATIONS]: createLoadingAction({
    method: loadRegions,
    dataKey: 'regions',
    settingDataMutation: mutationTypes.SET_OFFICE_LOCATIONS,
    settingLoadingMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING,
    settingErrorMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING_ERRORS,
  }),
  [types.LOAD_AMOUNT_OF_EMPLOYEES]: createLoadingAction({
    method: loadAmountOfEmployees,
    dataKey: 'amountOfEmployees',
    settingDataMutation: mutationTypes.SET_AMOUNT_OF_EMPLOYEES,
    settingLoadingMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING,
    settingErrorMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING_ERRORS,
  }),
  [types.LOAD_CYBERSECURITY_KNOWHOW_OPTIONS]: createLoadingAction({
    method: loadCybersecurityKnowhowOptions,
    dataKey: 'cybersecurityKnowhowOptions',
    settingDataMutation: mutationTypes.SET_CYBERSECURITY_KNOWHOW_OPTIONS,
    settingLoadingMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING,
    settingErrorMutation: mutationTypes.SET_COMPANY_REGISTRATION_INFO_LOADING_ERRORS,
  }),
  async [types.CHECK_IF_USER_POOL_EXISTS]({ commit }, domain) {
    const response = await findUserPoolId(domain);
    let isUserPoolExists = null;
    if (response) {
      isUserPoolExists = response;
    }
    commit(mutationTypes.SET_IS_USER_POOL_EXISTS, isUserPoolExists);
  },
  async [types.SET_IS_USER_POOL_EXISTS]({ commit }, isUserPoolExistsValue) {
    commit(SET_IS_USER_POOL_EXISTS, isUserPoolExistsValue);
  },
  async [types.GET_COMPANY_INFO]({ commit }, payload) {
    const company = await getCompanyInfo(payload);
    const {
      industry,
      regulations,
      region,
      amountOfEmployees,
      name,
      domain,
    } = company;
    const employeesObject = {
      amount: amountOfEmployees,
      text: i18n.t('registration.workspace.info.amount.of.employees', amountOfEmployees),
    };
    const industryObject = {
      value: industry.name,
    };
    const regionObject = {
      id: region.name,
    };
    const companyRegistrationData = {
      industry: industryObject,
      regulation: regulations,
      officeLocation: regionObject,
      employeesNumber: employeesObject,
      organizationName: { name, domain: domain?.toLowerCase() },
      organizationDomain: domain?.toLowerCase(),
    };
    commit(SET_COMPANY_REGISTRATION_DATA, companyRegistrationData);
  },
  async [types.REGISTER_USER]({ dispatch, commit, getters }) {
    try {
      // Set loading state
      commit(mutationTypes.SET_REGISTRATION_IN_PROGRESS, true);

      const {
        password,
        lastName,
        jobTitle,
        firstName,
        workEmail,
        phoneNumber,
        cybersecurityKnowhow,
      } = getters.registrationClientInfo;
      const {
        industry,
        regulation,
        officeLocation,
        employeesNumber,
        organizationName,
        organizationDomain,
      } = getters.registrationCompanyInfo;
      const {
        code,
        validated,
      } = getters.invitationValidationSuccessful;
      const { whiteLabeling } = getters;

      // Enhanced data validation
      if (!workEmail || !firstName || !lastName) {
        throw new Error('Required fields are missing. Please fill in all required information.');
      }

      if (!code || !validated) {
        throw new Error('Invalid invitation code. Please validate your invitation first.');
      }

      const userData = {
        workEmail,
        firstName,
        lastName,
        jobTitle: jobTitle || 'User', // Default job title
        password,
        phoneNumber,
        cybersecurityKnowhow: cybersecurityKnowhow?.name || 'basic', // Default value
        avatar: getters.registrationClientAvatar,

        company: {
          name: organizationName?.name || organizationDomain,
          domain: organizationDomain?.toLowerCase(),
          regulations: regulation || [],
          industryId: industry?.value || 'other',
          // Always send a valid enum value to the backend
          amountOfEmployees: employeesNumber?.amount || '1_TO_9',
          logo: organizationName?.logo || null,
          officeRegionId: officeLocation?.id || 'us-east-1', // Default region
        },

        invitationCode: {
          code,
          validated,
        },

        recaptchaToken: getters.registrationRecaptchaToken || 'skip-validation',
        timezone: currentTimeZone?.id || 'America/New_York',
        startTime: {
          time: getTimeWithMinuteOffset(2),
          date: moment().format(),
        },
        // Remove whiteLabeling if not needed (based on backend analysis)
        ...(whiteLabeling !== null && whiteLabeling !== undefined && { whiteLabeling }),
      };

      console.log('Registering user with data:', { ...userData, password: '[REDACTED]' });

      const result = await registerUser(userData);

      // Clear invitation data on success
      dispatch(CLEAN_INVITATION_CODE_DATA);

      return result;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      // Clear loading state
      commit(mutationTypes.SET_REGISTRATION_IN_PROGRESS, false);
    }
  },
  async [types.VALIDATE_INVITATION_CODE]({ commit, getters }, { code }) {
    let validationSucceeded = {};
    try {
      const recaptchaToken = getters.invitationValidationRecaptchaToken;
      const validationResponse = await validateInvitationCode(code, recaptchaToken);
      if (validationResponse && validationResponse.valid === true) {
        validationSucceeded = {
          validated: true,
          code,
          ...validationResponse,
        };
      } else {
        validationSucceeded = { validated: false, code: '' };
      }
    } catch (e) {
      validationSucceeded = { validated: false, code: '' };
      throw e;
    } finally {
      commit(SET_INVITATION_VALIDATION, validationSucceeded);
    }
  },
  async [types.REQUEST_EMAIL_VERIFICATION]({ commit, getters }, domain) {
    commit(mutationTypes.SET_EMAIL_VERIFICATION_REQUESTED, true);
    if (getters.isServiceProvider === true || getters.isServiceProvider.value === true) {
      try {
        const response = requestEmailVerificationForSP();
        return response;
      } catch (e) {
        console.log(e);
      }
    } // eslint-disable-next-line no-param-reassign
    domain = localStorage.getItem('domain');
    const userPoolId = await findUserPoolId({ domain });
    return requestEmailVerification(userPoolId);
  },
  async [types.VERIFY_EMAIL]({ getters }, options) {
    if (getters.isServiceProvider === true || getters.isServiceProvider.value === true) {
      return verifyEmailForSP(options.code);
    }
    const userPoolId = await findUserPoolId({ domain: localStorage.getItem('domain') });
    return verifyEmail(options.code, userPoolId);
  },
  async [types.CHANGE_WHITE_LABELING_INFO]({ commit }, whiteLabeling) {
    commit(mutationTypes.SET_WHITE_LABELING_INFO, whiteLabeling);
  },
  async [types.SET_REGISTRATION_USER_EMAIL]({ commit }, payload) {
    commit(mutationTypes.SET_REGISTRATION_USER_EMAIL, payload.email);
  },
};
